import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  inject,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { TuiDay } from "@taiga-ui/cdk";
import { TuiButton, TuiDialog, TuiTextfield } from "@taiga-ui/core";
import {
  TuiInputDateModule,
  TuiInputModule,
  TuiTextareaModule,
  TuiTextfieldControllerModule,
} from "@taiga-ui/legacy";
import { FeeService } from "../../../core/services/fee.service";
import { formatClientIdentifier } from "../../utils/client-identifier";

export type RenewalRecordInput = {
  _id?: string;
  date?: string | Date;
  fee?: number;
  notes?: string;
};

export type RecordRenewalConfirmPayload = {
  renewalDate: string;
  fee: number;
  notes?: string;
  renewalId?: string;
};

@Component({
  selector: "app-record-renewal-dialog",
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TuiButton,
    TuiDialog,
    TuiTextfield,
    TuiInputDateModule,
    TuiInputModule,
    TuiTextareaModule,
    TuiTextfieldControllerModule,
  ],
  templateUrl: "./record-renewal-dialog.component.html",
})
export class RecordRenewalDialogComponent implements OnChanges {
  private readonly feeService = inject(FeeService);

  @Input() open = false;
  @Input() client: {
    _id?: string;
    name?: string;
    expiryDate?: string | Date;
    registrationType?: string;
  } | null = null;
  /** When set, dialog edits an existing renewal record instead of creating one. */
  @Input() editRecord: RenewalRecordInput | null = null;
  @Input() busy = false;

  @Output() openChange = new EventEmitter<boolean>();
  @Output() confirm = new EventEmitter<RecordRenewalConfirmPayload>();
  @Output() cancel = new EventEmitter<void>();

  renewalDate: TuiDay | null = null;
  fee = 0;
  notes = "";
  systemRenewalFee = 0;

  get isEditMode(): boolean {
    return !!this.editRecord?._id;
  }

  get dialogTitle(): string {
    return this.isEditMode ? "Edit renewal" : "Record renewal";
  }

  get confirmLabel(): string {
    if (this.busy) return "Saving…";
    return this.isEditMode ? "Save changes" : "Record renewal";
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes["open"]?.currentValue === true) {
      if (this.isEditMode) {
        this.loadFromRecord(this.editRecord!);
      } else {
        this.resetForm();
        this.loadSystemFee();
      }
    }
  }

  clientLabel(): string {
    if (!this.client) return "";
    const id = formatClientIdentifier(this.client);
    const name = this.client.name || "";
    return id ? `${id} — ${name}` : name;
  }

  newExpiryPreview(): string {
    if (!this.renewalDate) return "—";
    try {
      const native = this.renewalDate.toLocalNativeDate();
      const expiry = new Date(native);
      expiry.setFullYear(expiry.getFullYear() + 1);
      return expiry.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch {
      return "—";
    }
  }

  onConfirm(): void {
    if (!this.renewalDate || this.busy) return;
    const native = this.renewalDate.toLocalNativeDate();
    const payload: RecordRenewalConfirmPayload = {
      renewalDate: this.formatCalendarDate(native),
      fee: Number(this.fee),
      notes: this.notes?.trim() || undefined,
    };
    if (this.isEditMode && this.editRecord?._id) {
      payload.renewalId = String(this.editRecord._id);
    }
    this.confirm.emit(payload);
  }

  /** Calendar date only (avoids UTC shift when comparing duplicate days). */
  private formatCalendarDate(native: Date): string {
    const y = native.getFullYear();
    const m = String(native.getMonth() + 1).padStart(2, "0");
    const d = String(native.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  }

  onCancel(): void {
    this.open = false;
    this.openChange.emit(false);
    this.cancel.emit();
  }

  onDialogClose(): void {
    this.openChange.emit(false);
  }

  private resetForm(): void {
    this.renewalDate = TuiDay.fromLocalNativeDate(new Date());
    this.notes = "";
  }

  private loadFromRecord(record: RenewalRecordInput): void {
    if (record.date) {
      const d = new Date(record.date);
      if (!Number.isNaN(d.getTime())) {
        this.renewalDate = TuiDay.fromLocalNativeDate(d);
      }
    }
    this.fee = record.fee != null ? Number(record.fee) : 0;
    this.notes = record.notes || "";
    this.systemRenewalFee = this.fee;
  }

  private loadSystemFee(): void {
    this.feeService.getCurrentFee().subscribe({
      next: (res: any) => {
        if (res?.success && res?.data) {
          this.systemRenewalFee = res.data.renewalFee || 0;
          this.fee = this.systemRenewalFee;
        }
      },
      error: () => {
        this.fee = 0;
      },
    });
  }
}
